import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type {
  FulfillmentStatus,
  FulfillmentType,
  OrderStatus,
  PaymentMethod,
  PaymentStatus,
  PickupStatus,
} from '@/lib/api-admin/types';
import type { OrderMall } from '@/features/orders/lib/orderMall';
import type { OrderLabelCode } from '@/features/orders/lib/displayLabelFilter';
import { DEFAULT_ORDER_SEARCH_TYPE, type OrderSearchType } from '@/features/orders/lib/orderSearchType';

export type OrderSort =
  | 'orderedAt,desc'
  | 'orderedAt,asc'
  | 'paymentAmount,desc'
  | 'paymentAmount,asc';

export const ORDER_SORTABLE_COLUMNS = new Set(['orderedAt', 'paymentAmount']);

/**
 * ID64: 주문상태/결제상태/배송상태/픽업상태/결제수단 필터를 단일값 → **다중선택**으로 확장.
 * BE(ID64) 계약: 복수형 param(`orderStatuses` 등)을 반복 query param 으로 전송하면 `in()` 필터.
 * 빈 배열 = 미적용(전체).
 *
 * QA-611 검색바는 체크박스를 고른 뒤 [검색]을 눌러야 조회한다 — 검색바가 고친 값을 들고 있다가 `apply` 로 한 번에 넣는다.
 */
export interface OrderFiltersState {
  /** QA-611 검색어 유형(주문번호·상품명·상품코드·자체상품코드). */
  searchType: OrderSearchType;
  /** 검색어 — searchType 에 따라 BE 쿼리가 달라진다(orderKeywordQuery). */
  q: string;
  ordererName: string;
  ordererPhone: string;
  /**
   * 국가몰(country-mall) 다중 필터 — BE `?malls=` 계약. 빈 배열 = 전체(미적용).
   * 값 = OrderMall 코드(BCP-47 ko-KR/ja-JP/en-US/zh-CN).
   */
  malls: ReadonlyArray<OrderMall>;
  paymentMethods: ReadonlyArray<PaymentMethod>;
  /** 주문상태(노출 상태) — 목록 상태 칸과 같은 기준(BE displayLabelCodes). 빈 배열 = 전체. */
  displayLabelCodes: ReadonlyArray<OrderLabelCode>;
  orderStatuses: ReadonlyArray<OrderStatus>;
  paymentStatuses: ReadonlyArray<PaymentStatus>;
  /** 이행 유형 — BE 는 단일축(fulfillmentType)이라 하나만 골랐을 때만 보낸다. 빈 배열 = 전체. */
  fulfillmentTypes: ReadonlyArray<FulfillmentType>;
  fulfillmentStatuses: ReadonlyArray<FulfillmentStatus>;
  pickupStatuses: ReadonlyArray<PickupStatus>;
  dateFrom: string | null;
  dateTo: string | null;
  amountMin: number | null;
  amountMax: number | null;
  page: number;
  size: number;
  sort: OrderSort;
  selectedIds: ReadonlyArray<string>;
  /** 검색 조건을 한 번에 넣고 1쪽으로. */
  apply: (filters: OrderFilterInputs) => void;
  setPage: (page: number) => void;
  setSize: (size: number) => void;
  setSort: (sort: OrderSort) => void;
  toggleSelected: (id: string) => void;
  setSelected: (ids: ReadonlyArray<string>) => void;
  clearSelection: () => void;
  reset: () => void;
}

type OrderFiltersData = Omit<
  OrderFiltersState,
  | 'apply'
  | 'setPage'
  | 'setSize'
  | 'setSort'
  | 'toggleSelected'
  | 'setSelected'
  | 'clearSelection'
  | 'reset'
>;

/** 검색바가 고치는 값(쪽·정렬·선택 제외). */
export type OrderFilterInputs = Omit<OrderFiltersData, 'page' | 'size' | 'sort' | 'selectedIds'>;

/** 로컬 Date → 'YYYY-MM-DD' (date input·BE dateFrom/dateTo 계약 포맷). */
export const toIsoDate = (d: Date): string => {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/**
 * be-perf 성능 계약: 기본 조회를 최근 3개월 윈도우로 좁혀 sargable range scan 경로를 탄다
 * (전체기간 deep-pagination 49s 회피). from=오늘-3개월, to=오늘. 운영자가 자유 변경 가능.
 * 매 호출 시 현재 날짜로 계산(reset·재마운트가 최신 윈도우를 얻도록 const 가 아닌 함수).
 */
export const defaultOrderDateWindow = (now: Date = new Date()): { dateFrom: string; dateTo: string } => {
  const from = new Date(now);
  from.setMonth(from.getMonth() - 3);
  return { dateFrom: toIsoDate(from), dateTo: toIsoDate(now) };
};

/** QA-537 페이지당 N개 보기(고도몰과 같은 선택지). 고른 값은 이 브라우저에 기억하고, [초기화]로는 바뀌지 않는다. */
export const ORDER_PAGE_SIZES: ReadonlyArray<number> = [10, 20, 30, 40, 50, 60, 70, 80, 90, 100, 200, 300, 500];
const PAGE_SIZE_STORAGE_KEY = 'order-list-page-size';
const DEFAULT_PAGE_SIZE = 20;

const readPageSize = (): number => {
  try {
    const n = Number(window.localStorage.getItem(PAGE_SIZE_STORAGE_KEY));
    return ORDER_PAGE_SIZES.includes(n) ? n : DEFAULT_PAGE_SIZE;
  } catch {
    return DEFAULT_PAGE_SIZE;
  }
};

const savePageSize = (size: number): void => {
  try {
    window.localStorage.setItem(PAGE_SIZE_STORAGE_KEY, String(size));
  } catch {
    // 저장 불가(프라이빗 모드 등) — 이번 화면에서만 쓴다.
  }
};

/** [초기화] 값 — 검색어 유형 주문번호·입력 비움·체크박스 전체·기간 3개월. */
export const initialOrderFilterInputs = (): OrderFilterInputs => {
  const { dateFrom, dateTo } = defaultOrderDateWindow();
  return {
    searchType: DEFAULT_ORDER_SEARCH_TYPE,
    q: '',
    ordererName: '',
    ordererPhone: '',
    malls: [],
    paymentMethods: [],
    displayLabelCodes: [],
    orderStatuses: [],
    paymentStatuses: [],
    fulfillmentTypes: [],
    fulfillmentStatuses: [],
    pickupStatuses: [],
    // be-perf 3개월 기본 윈도우(빠른 경로). null 이 아니라 기본 범위로 진입.
    dateFrom,
    dateTo,
    amountMin: null,
    amountMax: null,
  };
};

const initialFilters = (): OrderFiltersData => ({
  ...initialOrderFilterInputs(),
  page: 1,
  size: readPageSize(),
  sort: 'orderedAt,desc',
  selectedIds: [],
});

export const toggleOrderSort = (current: OrderSort, columnId: string): OrderSort => {
  if (!ORDER_SORTABLE_COLUMNS.has(columnId)) return current;
  const [id, dir] = current.split(',');
  if (id !== columnId) return `${columnId},desc` as OrderSort;
  return `${columnId},${dir === 'desc' ? 'asc' : 'desc'}` as OrderSort;
};

export const useOrderFiltersStore = create<OrderFiltersState>()(
  devtools(
    (set) => ({
      ...initialFilters(),
      apply: (filters) => set({ ...filters, page: 1, selectedIds: [] }, false, 'apply'),
      setPage: (page) => set({ page }, false, 'setPage'),
      setSize: (size) => {
        savePageSize(size);
        set({ size, page: 1 }, false, 'setSize');
      },
      setSort: (sort) => set({ sort, page: 1 }, false, 'setSort'),
      toggleSelected: (id) =>
        set(
          (s) => ({
            selectedIds: s.selectedIds.includes(id)
              ? s.selectedIds.filter((x) => x !== id)
              : [...s.selectedIds, id],
          }),
          false,
          'toggleSelected',
        ),
      setSelected: (ids) => set({ selectedIds: ids }, false, 'setSelected'),
      clearSelection: () => set({ selectedIds: [] }, false, 'clearSelection'),
      // 초기화 = 3개월 기본 윈도우 복원(전체기간 null 아님 → 빠른 경로 유지). 쪽 크기는 그대로.
      reset: () => set((s) => ({ ...initialFilters(), size: s.size }), false, 'reset'),
    }),
    { name: 'order-filters' },
  ),
);
